import 'css/src/components/public-pages/ContentfulPage';

import React from 'react';

import JumboCta from 'js/src/components/common/JumboCta';
import CaseStudyComponent from 'js/src/components/public-pages/contentful-components/CaseStudyComponent';
import CenteredMediaComponent from 'js/src/components/public-pages/contentful-components/CenteredMediaComponent';
import FAQComponent from 'js/src/components/public-pages/contentful-components/FAQComponent';
import ThirdPartyScriptComponent from 'js/src/components/public-pages/contentful-components/ThirdPartyScriptComponent';
import ContentfulHeader from 'js/src/components/public-pages/ContentfulHeader';
import Hero from 'js/src/components/public-pages/Hero';
import ImageAndText from 'js/src/components/public-pages/ImageAndText';
import PageListing from 'js/src/components/public-pages/PageListing';
import SixIcons from 'js/src/components/public-pages/SixIcons';
import ThreeColumnText from 'js/src/components/public-pages/ThreeColumnText';
import Title from 'js/src/components/public-pages/Title';
import TripleCard from 'js/src/components/public-pages/TripleCard';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { ContentfulPageType } from 'js/src/libs/contentful-utils';

const COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
    heroComponent: Hero,
    sixIconsComponent: SixIcons,
    imageAndTextComponent: ImageAndText,
    threeColumnTextComponent: ThreeColumnText,
    titleComponent: Title,
    tripleCardComponent: TripleCard,
    pageListingComponent: PageListing,
    thirdPartyScriptComponent: ThirdPartyScriptComponent,
    centeredMediaComponent: CenteredMediaComponent,
    faqComponent: FAQComponent,
    caseStudyComponent: CaseStudyComponent,
};

const WHITE = '#FFF';
const LIGHT_GREY = '#FAFAFA';

const getBgColorFor = (
    componentList: Immutable.List<ImmutableMap<Record<string, unknown>>>,
    index: number,
) => {
    // The title should have the same bg color as the content below,
    // other than that, the background color should be toggled for every
    // component.
    let bgColor = WHITE;
    for (let i = 0; i < index; i++) {
        const component = componentList.get(i);
        const ComponentClass = COMPONENT_MAP[component?.get('_content_type') as string];
        const isTitle = ComponentClass === Title;
        if (!isTitle) {
            bgColor = bgColor === WHITE ? LIGHT_GREY : WHITE;
        }
    }
    return bgColor;
};

const ContentfulPage = React.memo(function ContentfulPage() {
    const contentfulData = React.useState(
        ImmutableFromJS(JSON.parse(document.getElementById('contentful-content')!.innerText)),
    )[0] as ImmutableMap<ContentfulPageType>; // we don't ever want to use the setter for this data, so don't even assign it to a variable.

    const components = contentfulData.get('content_components');
    const componentsList = components.map((componentData, index) => {
        const ComponentClass = COMPONENT_MAP[componentData.get('_content_type') as string];
        const isBelowTheFold = index > 1; // the first 2 components are visible on page load
        return (
            <ComponentClass
                data={componentData}
                bgColor={getBgColorFor(components, index)}
                key={componentData.get('id')}
                lazyLoad={isBelowTheFold}
            />
        );
    });

    return (
        <>
            <ContentfulHeader pageData={contentfulData} />
            <div className="contentful-page">
                {componentsList}
                <div className="mb-l5-48">
                    <JumboCta
                        header="Join over 4,000,000 companies using Lumen5 to tell their stories."
                        body="Lumen5 is a video creation software that helps marketers, publishers, and brands create video content in a breeze, without any technical expertise."
                    />
                </div>
            </div>
        </>
    );
});

export default ContentfulPage;
